import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Archive, Copy, Loader2, Mail, RefreshCw, Trash2, ExternalLink, AlertTriangle } from "lucide-react";

// Page « Liens de paiement en attente » (demande du propriétaire, 2026-11-04,
// après le bogue URGENT du lien expiré de Valerie Guillet — « Vous avez terminé »).
// Liste toutes les soumissions avec un lien Stripe en attente, montre si le lien
// est EN DIRECT ou EXPIRÉ (statut en direct chez Stripe), et offre les actions :
// RELANCER (lien frais), COPIER, EXPIRER, ARCHIVER, SUPPRIMER.
const fmt = (n) => (Number(n) || 0).toFixed(2) + " $";

export default function PaymentLinksTab() {
  const [liens, setLiens] = useState([]);
  const [nbExpires, setNbExpires] = useState(0);
  const [chargement, setChargement] = useState(true);
  const [busy, setBusy] = useState("");

  const charger = () => {
    setChargement(true);
    api.get("/admin/payment-links")
      .then((r) => { setLiens(r.data.liens || []); setNbExpires(r.data.nb_expires || 0); })
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setChargement(false));
  };
  useEffect(charger, []);

  const copier = (url) => {
    navigator.clipboard?.writeText(url).then(() => toast.success("Lien copié"));
  };

  const relancer = async (l) => {
    if (!window.confirm(`Régénérer un lien FRAIS pour ${l.name} (${fmt(l.amount)}) ? L'ancien lien sera expiré.`)) return;
    setBusy(l.id);
    try {
      const r = await api.post(`/admin/quote-requests/${l.id}/payment-link/relaunch`);
      toast.success("Lien frais régénéré — copiez-le ou envoyez-le par courriel (fiche).");
      charger();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy("");
  };

  const expirer = async (l) => {
    if (!window.confirm(`Expirer le lien de ${l.name} ? Le client ne pourra plus l'utiliser.`)) return;
    setBusy(l.id);
    try {
      await api.post(`/admin/quote-requests/${l.id}/payment-link/expire`);
      toast.success("Lien expiré");
      charger();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy("");
  };

  const archiver = async (l) => {
    setBusy(l.id);
    try {
      await api.post(`/admin/quote-requests/${l.id}/payment-link/archive`);
      setLiens((p) => p.filter((x) => x.id !== l.id));
      toast.success("Lien archivé");
    } catch (err) { toast.error("Archivage impossible"); }
    setBusy("");
  };

  const supprimer = async (l) => {
    if (!window.confirm(`SUPPRIMER le lien de ${l.name} (${fmt(l.amount)}) ? La soumission reste, mais le lien est retiré et expiré chez Stripe.`)) return;
    setBusy(l.id);
    try {
      await api.delete(`/admin/quote-requests/${l.id}/payment-link`);
      setLiens((p) => p.filter((x) => x.id !== l.id));
      toast.success("Lien supprimé");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div className="bg-surface border border-ivory/10 rounded-2xl p-6" data-testid="payment-links-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="font-serif text-xl flex items-center gap-2">
          <ExternalLink className="w-5 h-5 text-gold" /> Liens de paiement en attente
        </h3>
        <div className="flex items-center gap-3">
          {nbExpires > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-400 bg-red-500/10 border border-red-400/30 rounded-full px-3 py-1"
              data-testid="payment-links-expires-count">
              <AlertTriangle className="w-3.5 h-3.5" /> {nbExpires} expiré{nbExpires > 1 ? "s" : ""} — à relancer
            </span>
          )}
          <button type="button" onClick={charger} data-testid="payment-links-refresh"
            className="inline-flex items-center gap-1.5 text-xs border border-ivory/25 text-ivory/70 px-3 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
          </button>
        </div>
      </div>

      {chargement ? (
        <p className="text-ivory/50 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</p>
      ) : liens.length === 0 ? (
        <p className="text-ivory/50 text-sm">Aucun lien de paiement en attente.</p>
      ) : (
        <div className="space-y-2">
          {liens.map((l) => (
            <div key={l.id} data-testid={`payment-link-${l.id}`}
              className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${
                l.expire ? "border-red-400/40 bg-red-500/5" : "border-ivory/10 bg-ink/40"}`}>
              <div className="flex-1 min-w-48">
                <p className="text-sm font-semibold text-ivory truncate">{l.name}</p>
                <p className="text-[11px] text-ivory/50 truncate">{l.email}</p>
              </div>
              <p className="text-sm font-bold text-gold">{fmt(l.amount)}</p>
              <span data-testid={`payment-link-statut-${l.id}`}
                className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                  l.expire ? "text-red-400 bg-red-500/15 border border-red-400/30" : "text-emerald-300 bg-emerald-500/10 border border-emerald-400/30"}`}>
                {l.expire ? "Expiré" : (l.statut === "ouvert" ? "En direct" : l.statut || "?")}
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                <button type="button" onClick={() => copier(l.url)} data-testid={`payment-link-copy-${l.id}`} title="Copier le lien"
                  className="p-2 rounded-full border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => relancer(l)} disabled={busy === l.id} data-testid={`payment-link-relaunch-${l.id}`}
                  title="Relancer — régénérer un lien frais"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-gold text-ink hover:bg-champagne disabled:opacity-50 transition-colors">
                  {busy === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Relancer
                </button>
                {!l.expire && (
                  <button type="button" onClick={() => expirer(l)} disabled={busy === l.id} data-testid={`payment-link-expire-${l.id}`}
                    title="Expirer le lien"
                    className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-500 hover:text-ink transition-colors">
                    Expirer
                  </button>
                )}
                <button type="button" onClick={() => archiver(l)} disabled={busy === l.id} data-testid={`payment-link-archive-${l.id}`}
                  title="Archiver (sortir de la liste)"
                  className="p-2 rounded-full border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
                  <Archive className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => supprimer(l)} disabled={busy === l.id} data-testid={`payment-link-delete-${l.id}`}
                  title="Supprimer le lien"
                  className="p-2 rounded-full border border-red-400/40 text-red-400 hover:bg-red-500 hover:text-ink transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-ivory/35 mt-4">
        Les liens Stripe expirent après ~24 h. Un lien « Expiré » montre « Vous avez terminé » au client — cliquez « Relancer » pour en régénérer un frais et le renvoyer.
      </p>
    </div>
  );
}
